import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IReportingPeriodItem } from "../../model/report/ReportingPeriodItem";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import GetReport6DataInteractors from "../../interactor/report/GetReport6DataInteractors";
import { IReport6Applicant } from "../../model/report/Report6Applicant";
import Report6ApplicantPresentationModel, {
  TReport6ApplicantPresentationModel
} from "./model/Report6ApplicantPresentationModel";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import GetApplicantsInteractor from "../../interactor/report/GetApplicantsInteractor";
import { ReportConstants } from "../../common/Constants";

export interface TReport6Presenter extends TLoadingAwarePresenter {
  report6SelectedApplicantIds?: string[];
  report6SelectedReportingPeriod?: IReportingPeriodItem;
  report6ReportingPeriodList: IReportingPeriodItem[];
  report6Applicants: IReport6Applicant[];
  report6ReportApplicants?: TReport6ApplicantPresentationModel[];
}

export interface IReport6Presenter extends TReport6Presenter, TPresentable {
  onChangeReportingPeriod(reportingPeriodId: number): void;
  onApplicantsChange(selectedApplicantsId: any[]): void;
  getReport6XLSXLink(): string;
  getReport6PDF(): void;
  setReportingPeriods(reportingPeriodList?: IReportingPeriodItem[]): void;
  onApplicantSearch(fullName: string): void;
}

const defaultState: TReport6Presenter = {
  report6ReportingPeriodList: [],
  report6Applicants: []
};

const Report6Presenter = withStore<IReport6Presenter, TReport6Presenter>(
  ({ application, store, loader, translate }): IReport6Presenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TReport6Presenter>();

    const setReportingPeriods = (
      report6ReportingPeriodList?: IReportingPeriodItem[]
    ) => {
      _store.update({ report6ReportingPeriodList: report6ReportingPeriodList });
    };

    const onChangeReportingPeriod = (reportingPeriodId: number) => {
      const report6ReportingPeriodList =
        _store.getState<TReport6Presenter>().report6ReportingPeriodList;
      const report6SelectedReportingPeriod = report6ReportingPeriodList.find(
        (reportingPeriod) => reportingPeriod.id === reportingPeriodId
      );
      _store.update({
        report6SelectedReportingPeriod,
        report6ReportApplicants: undefined
      });
    };

    const getReport6XLSXLink = (): string => {
      const { report6SelectedApplicantIds, report6SelectedReportingPeriod } =
        _store.getState<TReport6Presenter>();
      let XLSXUrl = "/api/reports/report6/xlsx?";

      report6SelectedApplicantIds?.forEach((selectedApplicantId) => {
        XLSXUrl += `&applicantIds=${selectedApplicantId}`;
      });

      if (report6SelectedReportingPeriod) {
        XLSXUrl += `&reportingPeriodId=${report6SelectedReportingPeriod.id}`;
      }
      return XLSXUrl;
    };

    const onApplicantsChange = (report6SelectedApplicantIds: string[]) => {
      if (report6SelectedApplicantIds.length === 0) {
        _store.update({
          report6SelectedApplicantIds,
          report6Applicants: []
        });
      } else {
        _store.update({
          report6SelectedApplicantIds
        });
      }
    };

    const getReport6PDF = async () => {
      loader.start("report6Loader");
      try {
        const { report6SelectedApplicantIds, report6SelectedReportingPeriod } =
          _store.getState<IReport6Presenter>();
        if (report6SelectedApplicantIds) {
          const report6ReportApplicants = await _application.container
            .resolve<GetReport6DataInteractors>("getReport6Data")
            .execute(
              report6SelectedApplicantIds.map((selectedApplicantId: string) => {
                return Number(selectedApplicantId);
              }),
              report6SelectedReportingPeriod?.id
            );
          _store.update({
            report6ReportApplicants: report6ReportApplicants.map(
              (reportApplicant: IReport6Applicant) => {
                return Report6ApplicantPresentationModel(reportApplicant);
              }
            )
          });
        }
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
      loader.stop("report6Loader");
    };

    const onApplicantSearch = async (fullName: string) => {
      try {
        if (
          fullName.length >= ReportConstants.AUTOCOMPLETE.APPLICANT_MIN_LENGTH
        ) {
          let report6Applicants = await application.container
            .resolve<GetApplicantsInteractor>("getApplicants")
            .execute(fullName);
          _store.update({ report6Applicants: report6Applicants || [] });
        } else {
          _store.update({ report6Applicants: [] });
        }
      } catch (error) {
        application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      translate,
      application: _application,
      onChangeReportingPeriod,
      onApplicantsChange,
      getReport6XLSXLink,
      getReport6PDF,
      setReportingPeriods,
      onApplicantSearch
    };
  },
  defaultState
);

export default Report6Presenter;
